// src/screens/Marketplace/PaymentConfirmationScreen.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './PaymentConfirmationScreen.css';
import api from '../../api';
import backArrow from '../../assets/backarrow-icon.png';

/* ---------- Same pricing helper as ProductDetailScreen ---------- */
function computePricingView(p, piToUsdRate) {
  if (p?.pricingView && typeof p.pricingView.basePrice === 'number') {
    const base = +Number(p.pricingView.basePrice).toFixed(2);
    const eff =
      typeof p.pricingView.effectivePrice === 'number'
        ? +Number(p.pricingView.effectivePrice).toFixed(2)
        : base;
    const isOnSale = !!p.pricingView.isOnFlashSale && eff < base;
    const percent = isOnSale && base > 0 ? Math.max(0, Math.round((1 - eff / base) * 100)) : null;
    return { basePi: base, salePi: isOnSale ? eff : null, percentOff: percent, isOnSale };
  }

  if (typeof p?.effectivePricePi === 'number') {
    const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
    const base =
      isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
        ? +Number(p.usdPrice / piToUsdRate).toFixed(2)
        : typeof p?.price === 'number'
        ? +Number(p.price).toFixed(2)
        : null;
    const eff = +Number(p.effectivePricePi).toFixed(2);
    if (base != null) {
      const isOnSale = !!(p?.sale?.active || p?.isOnSale === true) && eff < base;
      const percent = isOnSale && base > 0 ? Math.max(0, Math.round((1 - eff / base) * 100)) : null;
      return { basePi: base, salePi: isOnSale ? eff : null, percentOff: percent, isOnSale };
    }
  }

  const isDynamic = p?.pricingType === 'dynamic' && typeof p?.usdPrice === 'number';
  const basePi =
    isDynamic && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? +Number(p.usdPrice / piToUsdRate).toFixed(2)
      : typeof p?.price === 'number'
      ? +Number(p.price).toFixed(2)
      : null;

  const sale = p?.sale || p?.flashSale || p?.activeSale || {};
  const saleActive =
    !!sale?.active ||
    typeof p?.activeSalePricePi === 'number' ||
    typeof p?.activeSalePriceUsd === 'number' ||
    typeof p?.salePrice === 'number' ||
    typeof p?.salePricePi === 'number' ||
    typeof p?.salePriceUsd === 'number';

  if (!saleActive || basePi == null) return { basePi, salePi: null, percentOff: null, isOnSale: false };

  let salePi = null;
  if (typeof sale?.pricePi === 'number') salePi = sale.pricePi;
  else if (typeof p?.activeSalePricePi === 'number') salePi = p.activeSalePricePi;
  else if (typeof p?.salePricePi === 'number') salePi = p.salePricePi;

  const usdToPi = (usd) =>
    typeof usd === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0
      ? usd / piToUsdRate
      : null;

  if (salePi == null) {
    if (typeof sale?.priceUsd === 'number') salePi = usdToPi(sale.priceUsd);
    else if (typeof p?.activeSalePriceUsd === 'number') salePi = usdToPi(p.activeSalePriceUsd);
    else if (typeof p?.salePriceUsd === 'number') salePi = usdToPi(p.salePriceUsd);
  }
  if (salePi == null && typeof p?.salePrice === 'number') salePi = p.salePrice;

  if (salePi == null) return { basePi, salePi: null, percentOff: null, isOnSale: false };

  const finalSalePi = Math.max(0, +Number(salePi).toFixed(2));
  const percentOff = Math.max(0, Math.min(100, Math.round((1 - finalSalePi / basePi) * 100)));
  const isOnSale = finalSalePi < basePi;

  return { basePi, salePi: isOnSale ? finalSalePi : null, percentOff: isOnSale ? percentOff : null, isOnSale };
}
/* ---------------------------------------------------------------- */

function PaymentConfirmationScreen({ user }) {
  const { id: productId } = useParams();
  const navigate = useNavigate();

  const isMainnetHost = useMemo(() => {
    const h = (window?.location?.host || '').toLowerCase();
    const resolved = !h.includes('testnet') && !h.includes('sandbox');
    console.log('🧭 [PayScreen] host=%s | resolved=%s', h, resolved ? 'mainnet' : 'testnet');
    return resolved;
  }, []);

  const [product, setProduct] = useState(null);
  const [piToUsdRate, setPiToUsdRate] = useState(null);

  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [buyerDeliveryAddress, setBuyerDeliveryAddress] = useState('');
  const [buyerNote, setBuyerNote] = useState('');

  // 🔒 Quote state (uses backend cached rate; no external calls)
  const [quote, setQuote] = useState(null); // { id, amountPi, rateUsed, expiresAtISO }
  const [quoteStatus, setQuoteStatus] = useState('idle'); // idle | loading | ready | expired | error
  const [countdown, setCountdown] = useState(0);
  const countdownRef = useRef(null);

const [sellerExpired, setSellerExpired] = useState(false);
const [sellerStatusMsg, setSellerStatusMsg] = useState('');


  const isDynamic = useMemo(
    () => product?.pricingType === 'dynamic' && typeof product?.usdPrice === 'number',
    [product]
  );

  // Load product
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await api.get(`/products/${productId}`);
        const prod = res.data.product || res.data;
        setProduct(prod);
        console.log('🧾 [PayScreen] product snapshot:', prod);
      } catch (err) {
        console.error('❌ [PayScreen] Failed to load product:', err);
        setToastType('error');
        setToastMessage('Failed to load product. Please try again.');
        setTimeout(() => setToastMessage(''), 2500);
      }
    };
    fetchProduct();
  }, [productId]);


useEffect(() => {
  if (!product) return;

  // product.owner is populated in getProductById, so it should include merchantPaidUntil / isMerchant / merchantStatus
  const ownerObj = product.owner && typeof product.owner === 'object' ? product.owner : null;

  // If owner isn’t populated, don’t block on FE (backend will still block)
  if (!ownerObj) {
    setSellerExpired(false);
    setSellerStatusMsg('');
    return;
  }

  const paidUntil = ownerObj.merchantPaidUntil ? new Date(ownerObj.merchantPaidUntil).getTime() : null;

  const expired = !paidUntil || paidUntil <= Date.now();

  if (expired) {
    setSellerExpired(true);
    setSellerStatusMsg('Seller subscription expired. This item cannot be purchased right now.');
  } else {
    setSellerExpired(false);
    setSellerStatusMsg('');
  }
}, [product]);



  // Cached rate (for UI base conversion and non-dynamic)
  useEffect(() => {
    const fetchPiRate = async () => {
      try {
        const res = await api.get('/convert/rate');
        if (res.data?.piToUsd) setPiToUsdRate(res.data.piToUsd);
      } catch (err) {
        console.error('❌ Failed to fetch cached Pi rate:', err.message);
      }
    };
    fetchPiRate();
    const interval = setInterval(fetchPiRate, 600000);
    return () => clearInterval(interval);
  }, []);

  // Countdown util
  const startCountdown = (expiresAtISO) => {
    if (!expiresAtISO) return;
    const expireTs = new Date(expiresAtISO).getTime();
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      const now = Date.now();
      const secs = Math.max(0, Math.ceil((expireTs - now) / 1000));
      setCountdown(secs);
      if (secs <= 0) {
        clearInterval(countdownRef.current);
        setQuoteStatus('expired');
      }
    }, 250);
  };

  // Fetch quote (backend respects flash-sale + cached rate)
  const fetchQuote = async () => {
    if (!product || !isDynamic) {
      setQuote(null);
      setQuoteStatus('ready');
      return;
    }
    try {
      setQuoteStatus('loading');
      const res = await api.post(
        '/escrow/quote',
        { productId: product._id },
        { headers: { 'X-Network': 'mainnet' } }
      );
      const q = res.data;
      if (q?.amountPi && q?.expiresAtISO) {
        const locked = {
          id: q.id,
          amountPi: +Number(q.amountPi).toFixed(2),
          rateUsed: q.rateUsed ?? null,
          expiresAtISO: q.expiresAtISO,
        };
        setQuote(locked);
        setQuoteStatus('ready');
        startCountdown(q.expiresAtISO);
      } else {
        throw new Error('Malformed quote');
      }
    } catch (err) {
      console.warn('⚠️ Quote fetch failed:', err?.message);
      setQuote(null);
      setQuoteStatus('error');
    }
  };

  // Get quote when product loads / screen refocus
  useEffect(() => {
    if (!product) return;
    fetchQuote();
    const onFocus = () => fetchQuote();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?._id, isDynamic]);

  if (!product) return <div className="checkout-loading">Loading...</div>;

  // Standard pricing (non-dynamic rendering + fallback)
  const { basePi, salePi, percentOff, isOnSale } = computePricingView(product, piToUsdRate);
  const originalAmount = basePi != null ? basePi.toFixed(2) : null;
  const saleAmount = salePi != null ? salePi.toFixed(2) : null;

  // ===== Dynamic sale display (original vs sale + %) =====
  let dynBasePi = null;
  let dynSalePi = null;
  let dynPercent = null;
  let showDynamicSale = false;

  if (isDynamic) {
    // Prefer historical base snapshot if stored by backend
    const snapBase =
      product?.pricing && typeof product.pricing.originalPriceAtStart === 'number'
        ? product.pricing.originalPriceAtStart
        : null;

    // Else derive a base Pi from current cached rate (UI only)
    const derivedBase =
      snapBase != null
        ? snapBase
        : (typeof product?.usdPrice === 'number' && typeof piToUsdRate === 'number' && piToUsdRate > 0
            ? product.usdPrice / piToUsdRate
            : (typeof product?.price === 'number' ? product.price : null));

    const lockedSale = quote?.amountPi != null ? +Number(quote.amountPi).toFixed(2) : null;

    if (derivedBase != null && lockedSale != null && lockedSale < derivedBase - 0.005) {
      dynBasePi = +Number(derivedBase).toFixed(2);
      dynSalePi = lockedSale;
      dynPercent = Math.max(0, Math.round((1 - dynSalePi / dynBasePi) * 100));
      showDynamicSale = true;
    }
  }

  // Amount to pay uses quote for dynamic, else standard logic
  const dynamicPayPi = isDynamic && quote?.amountPi != null ? quote.amountPi : null;
  const payAmountPi = isDynamic ? dynamicPayPi : (salePi != null ? salePi : basePi);
  const youPayText = payAmountPi != null ? `${payAmountPi.toFixed(2)} Pi` : '…';

  const quoteIsActive = !isDynamic || quoteStatus === 'ready';

const payButtonDisabled =
  sellerExpired ||
  loading ||
  !isMainnetHost ||
  payAmountPi == null ||
  !quoteIsActive ||
  (isDynamic && countdown <= 0);

  const handleConfirm = async () => {

if (sellerExpired) {
  setToastType('error');
  setToastMessage('Seller subscription expired. You cannot purchase this item.');
  setTimeout(() => setToastMessage(''), 2500);
  return;
}


    if (!isMainnetHost) {
      setToastType('error');
      setToastMessage('Payments run on Mainnet only. Redirecting…');
      setTimeout(() => {
        window.location.href = 'https://barterfi.app';
      }, 1000);
      return;
    }

    if (isDynamic && (!quote || countdown <= 0)) {
      await fetchQuote();
      setToastType('error');
      setToastMessage('Refreshing live price…');
      setTimeout(() => setToastMessage(''), 1500);
      return;
    }

    if (payAmountPi == null) {
      setToastType('error');
      setToastMessage('Calculating price… please wait.');
      setTimeout(() => setToastMessage(''), 2000);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        buyer: user.username,
        seller: product.owner?.username || product.owner,
        productId: product._id,
        message: buyerNote,
        buyerDeliveryAddress,
        buyerNote,
        price: product.price, // USD if dynamic, Pi if raw — unchanged
        amount: Number(payAmountPi.toFixed(2)), // 🔒 exact amount shown
        meta: {
          pricingType: product.pricingType,
          originalPi: basePi != null ? Number(basePi.toFixed(2)) : null,
          salePi: salePi != null ? Number(salePi.toFixed(2)) : null,
          saleApplied: !!isOnSale || showDynamicSale,
          quoteId: quote?.id || null,
          rateUsed: quote?.rateUsed || null,
        },
      };

      const idem =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `escrow-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      await api.post('/escrow/create', payload, {
        headers: { 'X-Network': 'mainnet', 'X-Idempotency-Key': idem }
      });

      setToastType('success');
      setToastMessage('Escrow created successfully!');
      setTimeout(() => navigate('/orders', { replace: true }), 1500);
    } catch (err) {
      console.error('❌ [PayScreen] Payment failed:', err);
      const errorMsg = err?.response?.data?.error;
      setToastType('error');
      setToastMessage(errorMsg || 'Something went wrong. Try again.');
      setTimeout(() => setToastMessage(''), 2500);
      setLoading(false);
    }
  };

  return (
    <div className="checkout-container">
      <div className="checkout-header-fixed">
        <img
          src={backArrow}
          alt="Back"
          className="checkout-back-icon"
          onClick={() => navigate(-1)}
        />
        <h2>Payment Confirmation</h2>
      </div>

      <div className="checkout-scroll-content">
        <div className="checkout-summary">
          <img src={product.media?.[0]} alt="product" className="checkout-img" />
          <p className="checkout-name">{product.name}</p>
          <p className="checkout-store">Sold by @{product.owner?.username || product.owner}</p>

{sellerExpired && (
  <div className="checkout-expired-merchant">
    ⚠️ {sellerStatusMsg}
  </div>
)}


          {/* ✅ Pricing display */}
          <div className="checkout-pricing">
            <div className="checkout-price-row">
              {/* Dynamic + sale: show original (struck) + sale like raw Pi */}
              {isDynamic && showDynamicSale ? (
                <>
                  <span className="checkout-price original line-through">
                    {dynBasePi.toFixed(2)} Pi
                  </span>
                  <span className="checkout-price sale">
                    {dynSalePi.toFixed(2)} Pi
                  </span>
                </>
              ) : (
                // Non-dynamic (existing behavior)
                <>
                  {(!isDynamic && isOnSale && originalAmount && saleAmount) ? (
                    <>
                      <span className="checkout-price original line-through">{originalAmount} Pi</span>
                      <span className="checkout-price sale">{saleAmount} Pi</span>
                    </>
                  ) : (
                    <span className="checkout-price">
                      {youPayText}
                    </span>
                  )}
                </>
              )}
            </div>

            {/* % Off row for dynamic or non-dynamic */}
            {(isDynamic && showDynamicSale && typeof dynPercent === 'number') && (
              <div className="checkout-percent-row">-{dynPercent}%</div>
            )}
            {(!isDynamic && isOnSale && typeof percentOff === 'number') && (
              <div className="checkout-percent-row">-{percentOff}%</div>
            )}
          </div>

          <p className="checkout-rate-note">
            🔒 Payments are processed on <b>Mainnet</b> (barterfi.app)
          </p>

          {/* 🔒 Live-quote badge for dynamic pricing */}
          {isDynamic && (
            <div className="live-quote-row">
              {quoteStatus === 'loading' && <span>Fetching live price…</span>}
              {quoteStatus === 'error' && (
                <span>Couldn’t lock price. <button className="link-btn" onClick={fetchQuote}>Try again</button></span>
              )}
              {quoteStatus === 'ready' && (
                <span>
                  Price locked{typeof quote?.rateUsed === 'number' ? ` @ 1 Pi ≈ $${Number(quote.rateUsed).toFixed(2)}` : ''}.&nbsp;
                  Expires in <b>{String(Math.max(0, countdown)).padStart(2, '0')}s</b>.&nbsp;
                  <button className="link-btn" onClick={fetchQuote}>Refresh</button>
                </span>
              )}
              {quoteStatus === 'expired' && (
                <span>
                  Quote expired — <button className="link-btn" onClick={fetchQuote}>Get new price</button>
                </span>
              )}
            </div>
          )}
        </div>

        {product.category === 'physical' && (
          <div className="checkout-section">
            <label>Delivery Address (optional):</label>
            <input
              type="text"
              placeholder="e.g. 123 Main St"
              value={buyerDeliveryAddress}
              onChange={(e) => setBuyerDeliveryAddress(e.target.value)}
            />
          </div>
        )}

        <div className="checkout-section">
          <label>Your Username:</label>
          <input type="text" value={user.username} readOnly />
        </div>

        <div className="checkout-section">
          <label>Optional Message to Seller:</label>
          <textarea
            rows="3"
            placeholder="e.g. Please deliver before Monday"
            value={buyerNote}
            onChange={(e) => setBuyerNote(e.target.value)}
          />
        </div>

        <div className="checkout-pay-label">
          You pay: <strong>{youPayText}</strong>
        </div>
      </div>

      <div className="checkout-footer-fixed">
        <button
          className={`checkout-confirm-btn ${payButtonDisabled ? 'disabled' : ''}`}
          onClick={handleConfirm}
          disabled={payButtonDisabled}
        >
          {loading
            ? 'Processing…'
            : isMainnetHost
              ? (isDynamic ? 'Confirm & Pay (locked)' : 'Confirm & Pay')
              : 'Switch to Mainnet to Pay'}
        </button>
      </div>

      {toastMessage && (
        <div className={`slide-toast-banner ${toastType}`}>{toastMessage}</div>
      )}
    </div>
  );
}

export default PaymentConfirmationScreen;
